import { useExperimentTreatmentWithoutReporting } from '@ms/yammer-hooks/dist/experiments';
import { logScenarioStart, logScenarioStop } from '@ms/yammer-scenario-telemetry';
import type { ScenarioId, ScenarioStatus } from '@ms/yammer-scenario-telemetry';
import type { PageName } from '@ms/yammer-telemetry-support';
import { useEffect, useRef } from 'react';

import type { PageLoadEventData, PageLoadEventName } from '../../../telemetry/loadEvents';
import { subscribeToPageLoadEvent, unsubscribeFromPageLoadEvent } from '../../../telemetry/loadEvents';
import {
  getNavigationScenarioForPage,
  pageLoadOutcomeToScenarioStatus,
} from '../../../telemetry/navigationScenarioRegistry';

interface ActiveNavigationScenario {
  readonly scenarioId: ScenarioId;
  readonly page: PageName;
  readonly pageLoadId: number;
}

export interface UseNavigationScenarioTelemetryOptions {
  readonly page: PageName;
  readonly pageLoadId: number;
}

const pageLoadOutcomeEventNames = Object.keys(pageLoadOutcomeToScenarioStatus) as PageLoadEventName[];

const buildScenarioError = ({ errorMessage, errorCode }: PageLoadEventData): Error | undefined => {
  if (!errorMessage) {
    return undefined;
  }

  const error = new Error(errorMessage);
  if (errorCode) {
    error.name = errorCode;
  }

  return error;
};

/**
 * Emits scenario QoS telemetry for instrumented navigation destinations (see
 * `navigationScenarioRegistry`), gated by the `ScenarioBasedQosTelemetry` ECS flag.
 *
 * A scenario starts when a registered page begins loading and stops when that page load
 * terminates, mapping the page-load outcome (`Loaded`/`TimedOut`/`Interrupted`/`Failed`)
 * onto the mobile-aligned scenario status. This enriches the existing page-load path rather
 * than emitting a competing metric, and reuses the M2 façade so web and mobile join on the
 * shared wire values.
 *
 * Terminal outcomes are correlated to the in-flight scenario by `pageLoadId`, so a late event
 * from a superseded load of the same page cannot stop a newer scenario. If the provider
 * unmounts while a scenario is still in flight, it is reported as `incomplete` rather than
 * being left to auto-report as a `timeout`.
 *
 * NOTE: the scenario duration is measured from when the page load is observed here, so the
 * initial cold load slightly understates navigation-start latency. Anchoring to navigation
 * start is a fidelity follow-up.
 */
export const useNavigationScenarioTelemetry = ({ page, pageLoadId }: UseNavigationScenarioTelemetryOptions): void => {
  'use memo';

  const isEnabled = useExperimentTreatmentWithoutReporting('ScenarioBasedQosTelemetry');
  const activeScenarioRef = useRef<ActiveNavigationScenario | undefined>(undefined);

  useEffect(() => {
    const stopActiveScenario = (status: ScenarioStatus, error?: Error) => {
      const active = activeScenarioRef.current;
      if (!active) {
        return;
      }

      logScenarioStop({ scenarioId: active.scenarioId, status, error });
      activeScenarioRef.current = undefined;
    };

    const subscriptions = pageLoadOutcomeEventNames.map((eventName) => {
      const stopScenarioForOutcome = (data: PageLoadEventData) => {
        const active = activeScenarioRef.current;
        if (!active || data.pageLoadId !== active.pageLoadId) {
          return;
        }

        const status = pageLoadOutcomeToScenarioStatus[eventName];
        stopActiveScenario(status, status === 'failure' ? buildScenarioError(data) : undefined);
      };

      subscribeToPageLoadEvent(eventName, stopScenarioForOutcome);

      return { eventName, stopScenarioForOutcome };
    });

    return () => {
      subscriptions.forEach(({ eventName, stopScenarioForOutcome }) => {
        unsubscribeFromPageLoadEvent(eventName, stopScenarioForOutcome);
      });
      stopActiveScenario('incomplete');
    };
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const scenario = getNavigationScenarioForPage(page);
    if (!scenario) {
      return;
    }

    const active = activeScenarioRef.current;
    if (active?.pageLoadId === pageLoadId) {
      return;
    }

    if (active) {
      logScenarioStop({ scenarioId: active.scenarioId, status: 'cancel' });
    }

    const scenarioId = logScenarioStart({ name: scenario.name, source: scenario.source });
    activeScenarioRef.current = { scenarioId, page, pageLoadId };
  }, [isEnabled, page, pageLoadId]);
};
